import { CanActivateFn, CanMatchFn, Routes } from '@angular/router';

import { NotFoundComponent } from './components/not-found/not-found.component';
import { inject } from '@angular/core';
import { AuthService } from './services/auth.service';
import { mayAdministerMeetings } from './utils/meeting-access';
import { crmGuard, studentViewGuard } from './utils/view-access';
import { LegacyRole } from './models/user-access.model';

const adminGuard: CanMatchFn = () => {
  return inject(AuthService).getUserRole() === LegacyRole.Admin;
};

// Muss mit dem gleichnamigen Guard in teams-zoom-config.routes.ts übereinstimmen.
const teamsUserGuard: CanActivateFn = () => {
  return mayAdministerMeetings(inject(AuthService));
};

const pdfSignerGuard: CanActivateFn = () => {
  return inject(AuthService).getUserPermissions().includes('Pdf.Sign');
};

// --- Routengruppen (erstes URL-Segment) ---
const AUTH_PATHS = new Set([
  'login',
  'forgotPassword',
  'checkMail',
  'passwordChanged',
  'changePassword',
]);

const WORKFLOW_PATHS = new Set(['workflows', 'workflow-designer', 'todos']);

const EAGER_PATHS = new Set([
  'excel-upload',
  'import',
  'roles',
  'pdf-signer',
]);

// --- Matcher ---
const matchAuth: CanMatchFn = (_route, segments) => {
  const first = segments[0]?.path ?? '';
  return AUTH_PATHS.has(first);
};

const matchWorkflow: CanMatchFn = (_route, segments) => {
  const first = segments[0]?.path ?? '';
  return WORKFLOW_PATHS.has(first);
};

const LEGAL_PATHS = new Set(['impressum', 'datenschutz']);

const matchLegal: CanMatchFn = (_route, segments) => {
  const first = segments[0]?.path ?? '';
  return LEGAL_PATHS.has(first);
};

const matchMenu: CanMatchFn = (_route, segments) => {
  const first = segments[0]?.path ?? '';

  // "/" leitet unten auf /login um
  if (first === '') return false;

  if (AUTH_PATHS.has(first)) return false;
  if (WORKFLOW_PATHS.has(first)) return false;
  if (EAGER_PATHS.has(first)) return false;

  // alles Übrige: Menü-Bundle
  return true;
};

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },

  // Auth-Bundle
  {
    path: '',
    canMatch: [matchAuth],
    loadChildren: () => import('./auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },

  // Öffentlicher Dozenten-Stundenplan (per Hash), eingebettet per <iframe> in die unveränderte
  // WordPress-Seite. Kein Guard, Privacy by Obscurity.
  {
    path: 'timetable-public',
    loadComponent: () =>
      import('./docent-timetable/components/public-docent-timetable/public-docent-timetable.component').then(
        (m) => m.PublicDocentTimetableComponent,
      ),
    data: { showNavbar: false, showFooter: false },
  },

  // Impressum, Datenschutz: ohne Anmeldung erreichbar (§ 5 DDG)
  {
    path: '',
    canMatch: [matchLegal],
    loadChildren: () => import('./legal/legal.routes').then((m) => m.LEGAL_ROUTES),
  },

  // außerhalb der Guards, sonst Umleitungsschleife
  {
    path: 'access-error',
    loadComponent: () =>
      import('./components/access-error/access-error.component').then(
        (m) => m.AccessErrorComponent,
      ),
    data: { showNavbar: false, showFooter: false },
  },

  {
    path: 'student',
    canMatch: [studentViewGuard],
    loadChildren: () =>
      import('./student-view/student-view.routes').then(
        (m) => m.STUDENT_ROUTES,
      ),
  },

  // alles Weitere: CRM, ein Guard für alle
  {
    path: '',
    canMatch: [crmGuard],
    children: [
      // Workflow-Bundle
      {
        path: '',
        canMatch: [matchWorkflow],
        loadChildren: () =>
          import('./workflow-lazy/workflow.routes').then((m) => m.WORKFLOW_ROUTES),
      },

      // Menü-Bundle
      {
        path: '',
        canMatch: [matchMenu],
        loadChildren: () => import('./menu/menu.routes').then((m) => m.MENU_ROUTES),
      },
      {
        path: 'settings',
        canActivate: [adminGuard],
        loadChildren: () =>
          import('./components/settings/settings.routes').then(
            (m) => m.SETTINGS_ROUTES,
          ),
      },
      {
        path: 'teams-zoom-config',
        canActivate: [teamsUserGuard],
        loadChildren: () =>
          import('./teams-zoom-config/teams-zoom-config.routes').then(
            (m) => m.TEAMS_ZOOM_CONFIG_ROUTES,
          ),
      },

      // lazy: ExcelJS nicht im Hauptbundle
      {
        path: 'excel-upload',
        loadComponent: () =>
          import('./components/teams-list/presence_list').then(
            (m) => m.ExcelUploadPageComponent,
          ),
      },
      // lazy: ExcelJS nicht im Hauptbundle
      {
        path: 'import',
        loadComponent: () =>
          import('./components/dozentenplanung-import/dozentenplanung-import.component').then(
            (m) => m.DozentenplanungImportComponent,
          ),
      },
      {
        path: 'roles',
        loadComponent: () =>
          import('./components/role-list/role-list.component').then(
            (m) => m.RoleListComponent,
          ),
      },
      {
        path: 'live-attendance',
        loadComponent: () =>
          import('./live-attendance/components/live-attendance/live-attendance.component').then(
            (m) => m.LiveAttendanceComponent,
          ),
      },
      {
        path: 'on-site-attendance',
        loadComponent: () =>
          import('./live-attendance/components/on-site-attendance/on-site-attendance.component').then(
            (m) => m.OnSiteAttendanceComponent,
          ),
      },
      {
        path: 'absence-notifications',
        loadComponent: () =>
          import('./components/menu/absence-notifications/absence-notifications.component').then(
            (m) => m.AbsenceNotificationsComponent,
          ),
      },
      {
        path: 'teams-attendance',
        loadComponent: () =>
          import('./components/teams-list/teams-attendance-page/teams-attendance-page.component').then(
            (m) => m.TeamsAttendancePageComponent,
          ),
      },

      // Lazy laden, um ngx-extended-pdf-viewer/@angular/cdk aus dem initialen Bundle
      // herauszuhalten; per pdfSignerGuard geschützt (nur Berechtigung 'Pdf.Sign' - Rolle 4 allein reicht nicht)
      {
        path: 'pdf-signer',
        canActivate: [pdfSignerGuard],
        loadComponent: () =>
          import('./pdf-signer/components/pdf-signer-page/pdf-signer-page.component').then(
            (m) => m.PdfSignerPageComponent,
          ),
      },
    ],
  },

  // Fallback
  { path: '**', component: NotFoundComponent },
];
